
import { useEffect, useState } from "react";
import { IPrincipal, ITableColumn } from "@interfaces/common.interfaces";
import ReportSection from "components/reports-common/report-section/report-section";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import { IRequestModel } from "@interfaces/reports/reports-common.interfaces";
import {
  ISharedLinksInWorkspacesByOwnerReportDetails,
  ISharedLinksInWorkspacesByOwnerReportItem,
} from "@interfaces/reports/shared-link-reports.interfaces";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import PrincipalPicker from "components/controls/principal-picker/principal-picker";
import { usePPA } from "context/ppa-context";
import { PPAPermissions } from "lib/permissions";
import LinkCell from "components/reports-common/report-results-grid-cells/link-cell/link-cell";
import PrincipalsCell from "components/reports-common/report-results-grid-cells/principals-cell/principals-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import CheckedCell from "components/reports-common/report-results-grid-cells/checked-cell/checked-cell";
import SharingLinkPermissionsTypeCell from "components/reports-common/report-results-grid-cells/shared-links/sharing-link-permissions-type-cell/sharing-link-permissions-type-cell";
import InvitationsCell from "components/reports-common/report-results-grid-cells/intivations-cell/invitations-cell";
import { escapeForExport } from "lib/utils";
import {
  getSharingLinkPermissionsTypePrimaryLabelKey,
  getSharingLinkPermissionsTypeSecondaryLabelKey,
} from "lib/shared-links";
import { RemoveSharingLinksActionButton } from "components/actions/sharing-links/remove-sharing-links-action/remove-sharing-links-action-button";
import { getWorkspaceTypeKey } from "lib/workspaces";

const reportUrl = "/api/reports/sharing-links-in-workspaces-by-owner";

export default function SharingLinksInWorkspacesByOwnerReport() {
  const { currentUser, hasPermission } = usePPA();
  const { t } = useL10n<ILocaleStrings>();

  const [model, setModel] = useState<IRequestModel>({});
  const [selectedUser, setSelectedUser] = useState<IPrincipal>();
  const [mineOnly, setMineOnly] = useState(false);

  useEffect(() => {
    setSelectedUser(currentUser);
    if (
      hasPermission(PPAPermissions.RequestSharingLinksInWorkspacesByOwnerReportMineOnly) &&
      !hasPermission(PPAPermissions.RequestSharingLinksInWorkspacesByOwnerReportAny)
    )
      setMineOnly(true);
  }, []);

  const onUserChange = (value?: IPrincipal) => {
    setModel({ ...model, owner: { upn: value?.upn } });
    setSelectedUser(value);
  };

  const cols: Array<ITableColumn<ISharedLinksInWorkspacesByOwnerReportItem>> = [
    {
      key: "workspaceName",
      label: t("ppaWorkspace"),
      showInGrid: true,
      showInExport: true,
      defaultWidth: 200,
      renderCell: (key, item) => <LinkCell label={item.workspaceName} url={item.workspaceUrl} />,
    },
    { key: "workspaceUrl", label: t("ppaWorkspaceUrl"), showInGrid: false, showInExport: true },
    {
      key: "workspaceType",
      label: t("ppaWorkspaceType"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => t(getWorkspaceTypeKey(item.workspaceType)),
      getExportValue: (k, item) => t(getWorkspaceTypeKey(item.workspaceType)),
    },

    {
      key: "itemName",
      label: t("ppaItem"),
      showInGrid: true,
      showInExport: true,
      defaultWidth: 200,
      renderCell: (k, item) => <LinkCell label={item.itemName} url={item.itemUrl} />,
    },
    { key: "itemUrl", label: t("ppaItemUrl"), showInGrid: false, showInExport: true },
    { key: "linkUrl", label: t("ppaLinkUrl"), showInGrid: false, showInExport: true, idealWidth: 300 },
    { key: "itemUniqueId", label: t("ppaItemUniqueId"), showInGrid: false, showInExport: true },
    { key: "shareId", label: t("ppaShareId"), showInGrid: false, showInExport: true },

    {
      key: "sharedBy",
      label: t("ppaSharedBy"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <PrincipalsCell columnId={k} item={item} />,
      getExportValue: (k, item) => escapeForExport(item.sharedBy),
    },
    {
      key: "sharedBy",
      label: t("ppaSharedByUpn"),
      showInGrid: false,
      showInExport: true,
      getExportValue: (k, item) => item.sharedBy.upn,
    },
    {
      key: "shared",
      label: t("ppaSharedOn"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <DateTimeCell columnId={k} item={item} pattern="PPp" />,
    },
    {
      key: "permissionType",
      label: t("ppaPermissions"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <SharingLinkPermissionsTypeCell permissionType={item.permissionType} />,
      getExportValue: (k, item) =>
        `"${t(getSharingLinkPermissionsTypePrimaryLabelKey(item.permissionType))} - ${t(
          getSharingLinkPermissionsTypeSecondaryLabelKey(item.permissionType)
        )}"`,
    },
    {
      key: "invitations",
      label: t("ppaInvitees"),
      showInGrid: true,
      showInExport: false,
      renderCell: (k, item) => <InvitationsCell columnId={k} item={item} />,
    },
    {
      key: "invitations",
      label: t("ppaInvitations"),
      showInGrid: false,
      showInExport: true,
      getExportValue: (k, item) => item.invitations?.length ? escapeForExport(item.invitations) : "",
    },
    {
      key: "invitations",
      label: t("ppaInviteeUpns"),
      showInGrid: false,
      showInExport: true,
      getExportValue: (k, item) => item.invitations.map((i) => i.invitee.upn || i.invitee.email).join(";"),
    },
    {
      key: "linkMembers",
      label: t("ppaMembers"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <PrincipalsCell columnId={k} item={item} />,
      getExportValue: (k, item) => item.linkMembers && item.linkMembers.length ? escapeForExport(item.linkMembers) : "",
    },
    {
      key: "linkMembers",
      label: t("ppaMemberUpns"),
      showInGrid: false,
      showInExport: true,
      getExportValue: (k, item) => item.linkMembers.map((m) => m.upn).join(";"),
    },
    {
      key: "expiration",
      label: t("ppaExpires"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <DateTimeCell columnId={k} item={item} pattern="PPp" />,
      getExportValue: (k, item) => item.expiration || "",
    },
    {
      key: "requiresPassword",
      label: t("ppaRequiresPassword"),
      showInGrid: false,
      showInExport: true,
      renderCell: (k, item) => <CheckedCell columnId={k} item={item} checkedValue={true} />,
      getExportValue: (k, item) => (item.requiresPassword ? t("yes") : t("no")),
    },
    { key: "passwordLastModified", label: t("ppaPasswordLastModified"), showInGrid: false, showInExport: true },
    {
      key: "allowsAnonymousAccess",
      label: t("ppaAnonymousAccess"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <CheckedCell columnId={k} item={item} checkedValue={true} />,
      getExportValue: (k, item) => (item.allowsAnonymousAccess ? t("yes") : t("no")),
    },
    {
      key: "hasExternalGuestInvitees",
      label: t("ppaHasGuests"),
      showInGrid: true,
      showInExport: true,
      renderCell: (k, item) => <CheckedCell columnId={k} item={item} checkedValue={true} />,
      getExportValue: (k, item) => (item.hasExternalGuestInvitees ? t("yes") : t("no")),
    },
  ];

  const isModelValid = () => !!model.owner?.upn;

  const reportSectionProps: IReportSectionProps<
    ISharedLinksInWorkspacesByOwnerReportItem,
    ISharedLinksInWorkspacesByOwnerReportDetails
  > = {
    columns: cols,
    summaryComponent: <></>,
    reportUrl,
    model,
    isModelValid: isModelValid(),
    noItemsMessage: t("ppaSharingLinksInWorkspacesByOwnerReportNoItemsMessage"),
    defaultExportFilenamePrefix: "ppaSharingLinksInWorkspacesByOwnerReportDefaultExportFilenamePrefix",
    selectionType: 'single',
    buttons: [
      {
        component: <RemoveSharingLinksActionButton />
      }
    ]
  };

  return (
    <ReportSection {...reportSectionProps}>
      <PrincipalPicker
        onChange={onUserChange}
        model={selectedUser}
        label="ppaWorkspaceRoleTypeOwner"
        readonly={mineOnly}
        principalTypes={["User"]}
      />
    </ReportSection>
  );
}
